import { type ExternalUserServiceIntegration } from '../../../../models/User'
import { type UserIntegrationIssue } from '../../../../store/user/user'
import { type IntegrationServiceId } from '../../../../common/integrations'
import { CalendarProvider, MeetingPlatformProvider, type UserIntegrations } from '../../../../models/Scheduling'

export enum IntegrationConnectionStatus {
  READY = 'ready',
  NEEDS_RECONNECT = 'needs_reconnect',
  NOT_CONNECTED = 'not_connected',
}

export interface IntegrationConnection {
  status: IntegrationConnectionStatus
  email?: string
}

export const getIntegrationConnection = (
  integrations: Array<ExternalUserServiceIntegration> | undefined,
  problems: Array<UserIntegrationIssue> | undefined,
  serviceId: IntegrationServiceId,
  /**
   * Determined by scheduling-be whether the user-integration has the required
   * scopes and permissions needed for scheduling to function properly
   */
  capable?: boolean,
): IntegrationConnection => {
  const integration = (integrations ?? []).find((candidate) => candidate.serviceId === serviceId)
  if (!integration) {
    return { status: IntegrationConnectionStatus.NOT_CONNECTED }
  }
  const hasProblem = (problems ?? []).some((problem) => problem.integrationId === integration.id)
  return {
    status: integration.state === 'live' && !hasProblem && capable !== false ? IntegrationConnectionStatus.READY : IntegrationConnectionStatus.NEEDS_RECONNECT,
    email: integration.email,
  }
}

export const getSchedulingCapability = (
  integrations: UserIntegrations | undefined,
  provider: CalendarProvider | MeetingPlatformProvider,
): boolean | undefined =>
  [...(integrations?.calendars ?? []), ...(integrations?.meetingPlatforms ?? [])].find((integration) => integration.provider === provider)?.capable

export const NATIVE_PLATFORM_BY_CALENDAR: Partial<Record<CalendarProvider, MeetingPlatformProvider>> = {
  [CalendarProvider.GOOGLE]: MeetingPlatformProvider.GOOGLE_MEET,
  [CalendarProvider.OUTLOOK]: MeetingPlatformProvider.MS_TEAMS,
}

export const isPlatformCompatibleWithCalendar = (platform: MeetingPlatformProvider, calendar: CalendarProvider): boolean =>
  platform === MeetingPlatformProvider.ZOOM || NATIVE_PLATFORM_BY_CALENDAR[calendar] === platform

export const resolveIntegrationsOnCalendarSelect = (
  current: UserIntegrations | undefined,
  calendar: CalendarProvider,
  isZoomSelectable: boolean,
): UserIntegrations => {
  const keepZoom = current?.selectedMeetingPlatform === MeetingPlatformProvider.ZOOM && isZoomSelectable
  return {
    ...current,
    selectedCalendar: calendar,
    selectedMeetingPlatform: keepZoom ? MeetingPlatformProvider.ZOOM : (NATIVE_PLATFORM_BY_CALENDAR[calendar] ?? current?.selectedMeetingPlatform),
  }
}
